import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatDateTime, formatNumber } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

import { StockMovementModal } from './StockMovementModal.jsx';

const MOVEMENT_BADGE_VARIANT = {
  IN: 'success',
  OUT: 'warning',
  TRANSFER_IN: 'info',
  TRANSFER_OUT: 'info',
  ADJUSTMENT: 'neutral',
};

const MOVEMENT_TYPE_ORDER = ['IN', 'OUT', 'TRANSFER_IN', 'TRANSFER_OUT', 'ADJUSTMENT'];

function movementTypeColor(palette, type) {
  if (type === 'IN' || type === 'TRANSFER_IN') return palette?.series?.[1];
  if (type === 'OUT' || type === 'TRANSFER_OUT') return palette?.expense;
  if (type === 'ADJUSTMENT') return palette?.series?.[2];
  return palette?.axis;
}

export function StockMovementsPage() {
  const { t } = useTranslation();
  const [openKind, setOpenKind] = useState(null);

  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items || []),
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items || []),
  });
  const movements = useQuery({
    queryKey: ['inventory', 'movements'],
    queryFn: () => inventoryApi.movements({ limit: 200 }).then((r) => r.items || []),
  });

  const productById = useMemo(() => {
    const map = new Map();
    for (const p of products.data || []) {
      map.set(p.id, p);
    }
    return map;
  }, [products.data]);

  const warehouseById = useMemo(() => {
    const map = new Map();
    for (const w of warehouses.data || []) {
      map.set(w.id, w);
    }
    return map;
  }, [warehouses.data]);

  const palette = useChartPalette();
  const rows = movements.data || [];
  const stats = useMemo(() => {
    const counts = countBy(rows, (r) => r.type);
    const types = [
      ...MOVEMENT_TYPE_ORDER.filter((type) => counts[type]),
      ...Object.keys(counts).filter((type) => !MOVEMENT_TYPE_ORDER.includes(type)),
    ];
    const inflow = (counts.IN || 0) + (counts.TRANSFER_IN || 0);
    const outflow = (counts.OUT || 0) + (counts.TRANSFER_OUT || 0);
    return {
      counts,
      types,
      inflow,
      outflow,
      totalValue: sumBy(rows, (r) => r.totalCost),
    };
  }, [rows]);

  const columns = [
    { key: 'occurredAt', label: t('inv.movements.col.date'), render: (r) => formatDateTime(r.occurredAt) },
    {
      key: 'productId',
      label: t('inv.movements.col.product'),
      render: (r) => {
        const product = productById.get(r.productId);
        return product ? `${product.sku} - ${product.name}` : r.productId.slice(0, 8);
      },
    },
    {
      key: 'warehouseId',
      label: t('inv.movements.col.warehouse'),
      render: (r) => warehouseById.get(r.warehouseId)?.code || r.warehouseId.slice(0, 8),
    },
    {
      key: 'type',
      label: t('inv.movements.col.type'),
      render: (r) => (
        <Badge variant={MOVEMENT_BADGE_VARIANT[r.type] || 'neutral'}>
          {t(`inv.movements.type.${r.type}`)}
        </Badge>
      ),
    },
    { key: 'quantity',  label: t('inv.movements.col.quantity'),  align: 'right', render: (r) => formatNumber(r.quantity) },
    { key: 'unitCost',  label: t('inv.movements.col.unitCost'),  align: 'right', render: (r) => formatCurrency(r.unitCost) },
    { key: 'totalCost', label: t('inv.movements.col.totalCost'), align: 'right', render: (r) => formatCurrency(r.totalCost) },
    { key: 'notes',     label: t('inv.movements.col.notes') },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inv.movements.title')}</h1>
          <p className="muted">{t('inv.movements.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setOpenKind('receive')}>{t('inv.movements.action.receive')}</Button>
          <Button variant="default" onClick={() => setOpenKind('issue')}>{t('inv.movements.action.issue')}</Button>
          <Button variant="default" onClick={() => setOpenKind('transfer')}>{t('inv.movements.action.transfer')}</Button>
          <Button variant="default" onClick={() => setOpenKind('adjust')}>{t('inv.movements.action.adjust')}</Button>
        </div>
      </header>
      {rows.length > 0 && (
        <InsightBar
          title={t('insight.byType')}
          labels={stats.types.map((type) => t(`inv.movements.type.${type}`))}
          values={stats.types.map((type) => stats.counts[type])}
          colors={stats.types.map((type) => movementTypeColor(palette, type))}
          metrics={[
            { label: t('insight.count'), value: rows.length },
            { label: t('inv.movements.insight.inflow'), value: stats.inflow },
            { label: t('inv.movements.insight.outflow'), value: stats.outflow },
            { label: t('inv.movements.insight.totalValue'), value: formatCurrency(stats.totalValue) },
          ]}
        />
      )}
      {movements.error && <div className="alert danger">{movements.error.message}</div>}
      <Table
        columns={columns}
        rows={movements.data}
        isLoading={movements.isLoading}
        emptyMessage={t('inv.movements.empty')}
      />
      <StockMovementModal
        kind={openKind}
        open={Boolean(openKind)}
        onClose={() => setOpenKind(null)}
      />
    </section>
  );
}
